import type { PDFPageProxy } from 'pdfjs-dist';

/**
 * Formatting for the document properties dialog, ported from pdf.js's own
 * `PDFDocumentProperties`. The generic viewer's helpers (`getPageSizeInches`,
 * `isPortraitOrientation`) are internal to `pdf_viewer.mjs` and not exported, so the
 * two of them are reproduced here — they are a handful of lines each.
 */

const LOCALE = 'de-AT';

/**
 * Formatters return null when the document does not carry the value. The dialog drops
 * those rows rather than printing a column of dashes — most lab PDFs leave Titel, Autor,
 * Thema and Stichwörter empty, and the old viewer's static markup had no choice but to
 * show them anyway.
 */
type Value = string | null;

const US_PAGE_NAMES: Record<string, string> = { '8.5x11': 'Letter', '8.5x14': 'Legal' };
const METRIC_PAGE_NAMES: Record<string, string> = { '297x420': 'A3', '210x297': 'A4' };

interface Size {
  width: number;
  height: number;
}

const getPageSizeInches = ({ view, userUnit, rotate }: PDFPageProxy): Size => {
  const [x1, y1, x2, y2] = view;
  const changeOrientation = rotate % 180 !== 0;
  const width = ((x2 - x1) / 72) * userUnit;
  const height = ((y2 - y1) / 72) * userUnit;
  return {
    width: changeOrientation ? height : width,
    height: changeOrientation ? width : height,
  };
};

const isPortrait = (size: Size): boolean => size.width <= size.height;

const pageName = (size: Size, portrait: boolean, names: Record<string, string>): string | undefined => {
  const width = portrait ? size.width : size.height;
  const height = portrait ? size.height : size.width;
  return names[`${width}x${height}`];
};

/** `1.234,5 KB (1.264.128 Bytes)`, mirroring pdf.js's own thresholds and precision. */
export const formatFileSize = (bytes: number | undefined): Value => {
  if (!bytes) {
    return null;
  }
  const kb = bytes / 1024;
  const mb = kb / 1024;
  const value = mb >= 1 ? mb : kb;
  const unit = mb >= 1 ? 'MB' : 'KB';
  return `${Number(value.toPrecision(3)).toLocaleString(LOCALE)} ${unit} (${bytes.toLocaleString(LOCALE)} Bytes)`;
};

/** PDF date strings look like `D:20240131120000+01'00'`. */
export const formatPdfDate = (value: unknown, toDate: (raw: string) => Date | null): Value => {
  if (typeof value !== 'string') {
    return null;
  }
  const date = toDate(value);
  if (!date) {
    return null;
  }
  return `${date.toLocaleDateString(LOCALE)} ${date.toLocaleTimeString(LOCALE)}`;
};

/** `210 × 297 mm (A4, Hochformat)` */
export const formatPageSize = (page: PDFPageProxy, pagesRotation: number): Value => {
  let inches = getPageSizeInches(page);
  if (pagesRotation % 180 !== 0) {
    inches = { width: inches.height, height: inches.width };
  }
  const portrait = isPortrait(inches);

  const millimeters = {
    width: Math.round(inches.width * 25.4 * 10) / 10,
    height: Math.round(inches.height * 25.4 * 10) / 10,
  };
  const rounded = {
    width: Math.round(inches.width * 100) / 100,
    height: Math.round(inches.height * 100) / 100,
  };

  const name = pageName(rounded, portrait, US_PAGE_NAMES) ?? pageName(millimeters, portrait, METRIC_PAGE_NAMES);
  const orientation = portrait ? 'Hochformat' : 'Querformat';
  const dimensions = `${millimeters.width.toLocaleString(LOCALE)} × ${millimeters.height.toLocaleString(LOCALE)} mm`;

  return name ? `${dimensions} (${name}, ${orientation})` : `${dimensions} (${orientation})`;
};

export const formatText = (value: unknown): Value => (typeof value === 'string' && value.trim() !== '' ? value : null);

export const formatLinearized = (value: unknown): Value => (value === true ? 'Ja' : 'Nein');
